/**
 * Dev-only zone routes (`q-playtest-cross-zone-jump`; docs/features/playtest-panel.md §8).
 *
 * The server hosts ONE zone per process. `npm run playtest:zones` boots one backend per zone and
 * hands the vite dev server `GC_DEV_ZONE_PORTS=<zone>=<port>,…`; `vite.config.ts` proxies
 * `/zone/<zone>/api` and `/zone/<zone>/ws` to that port, and the Playtest panel points the client's
 * runtime server URL at `<origin>/zone/<zone>` before a jump. Pure — shared by the vite config
 * (Node) and the client.
 */

/** The path prefix every zone route lives under. */
export const DEV_ZONE_PREFIX = '/zone/';

export interface DevZoneRoute {
  zone: string;
  port: number;
}

const ZONE_ID = /^[a-z0-9][a-z0-9-]*$/;

/**
 * Parse `GC_DEV_ZONE_PORTS` (`driftwell=8081,nimbus=8082`). A malformed pair is dropped rather
 * than failing the dev server — this is a convenience knob, like `GC_DEV_BACKEND_PORT`. A zone
 * named twice keeps its first port.
 */
export function parseDevZonePorts(raw: string | undefined): DevZoneRoute[] {
  if (raw === undefined) return [];
  const routes: DevZoneRoute[] = [];
  for (const pair of raw.split(',')) {
    const [zone, portText, ...rest] = pair.trim().split('=');
    if (rest.length > 0 || zone === undefined || portText === undefined) continue;
    const port = Number(portText);
    if (!ZONE_ID.test(zone) || !Number.isInteger(port) || port <= 0 || port > 65535) continue;
    if (routes.some((r) => r.zone === zone)) continue;
    routes.push({ zone, port });
  }
  return routes;
}

/** Format routes back into the env value (the launcher's side of the contract). */
export function formatDevZonePorts(routes: readonly DevZoneRoute[]): string {
  return routes.map((r) => `${r.zone}=${r.port}`).join(',');
}

/** The server base a jump into `zone` uses: `<origin>/zone/<zone>` (no trailing slash). */
export function zoneServerUrl(origin: string, zone: string): string {
  return `${origin.replace(/\/+$/, '')}${DEV_ZONE_PREFIX}${zone}`;
}

/** Vite `define` (vite.config.ts): the routed zone ids, comma list. Absent under the test runner. */
declare const __GC_DEV_ZONES__: string | undefined;

/** The routed zone ids the dev server exposes; [] if none (a plain `npm run dev`, or a test). */
export function routedDevZones(): string[] {
  const raw = typeof __GC_DEV_ZONES__ === 'string' ? __GC_DEV_ZONES__ : '';
  if (raw === '') return [];
  return raw.split(',').filter((zone) => ZONE_ID.test(zone));
}
